import React, { useEffect, useState } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { authenticatedFetch } from '../state/apiUtils';
import { slugify } from './Global/TenantPath';
import { AccessDenied } from './AccessDenied';
import Loading from './Global/loading';

export const AccountResolver = ({ children }: { children: React.ReactNode }) => {
    const { instance, accounts, inProgress } = useMsal();
    const account = useAccount(accounts[0] || {});
    const [status, setStatus] = useState<'checking' | 'ok' | 'denied'>('checking');

    useEffect(() => {
        if (inProgress !== InteractionStatus.None) return;
        if (!account) return;

        const resolve = async () => {
            try {
                let data;
                try {
                    

                    data = await authenticatedFetch('/auth/validate-account');
                    
                } catch (fetchError) {
                    if (fetchError instanceof InteractionRequiredAuthError) {
                        await instance.acquireTokenRedirect({
                            scopes: [process.env.REACT_APP_SCOPE || ''],
                            account,
                        });
                        return;
                    }

                    // First call after login is the most likely to hit a
                    // cold backend/database (Azure SQL serverless waking up,
                    // App Service cold start). Retry once after a short
                    // delay before treating this as a real denial, so a
                    // transient 500 doesn't get shown to the user as
                    // "Access Denied".
                    console.warn('[AccountResolver] validate-account failed, retrying once:', fetchError);
                    await new Promise((resolve) => setTimeout(resolve, 3000));

                    try {
                        data = await authenticatedFetch('/auth/validate-account');
                    } catch (retryError) {
                        console.error('[AccountResolver] validate-account failed again after retry:', retryError);
                        setStatus('denied');
                        return;
                    }
                }

                const accountName: string = data.accountName;
                const accountSlug = slugify(accountName);

                const segments = window.location.pathname.split('/').filter(Boolean);
                const firstSegmentRaw = segments[0] || '';
                const firstSegment = decodeURIComponent(firstSegmentRaw);
                const alreadyPrefixed = firstSegment.toLowerCase() === accountSlug.toLowerCase();

                if (!alreadyPrefixed) {
                    const lastRedirectTarget = sessionStorage.getItem('accountResolverRedirectTarget');
                    if (lastRedirectTarget === accountSlug) {
                        console.error('[AccountResolver] redirect loop detected — aborting instead of looping.');
                        setStatus('denied');
                        return;
                    }
                    sessionStorage.setItem('accountResolverRedirectTarget', accountSlug);

                    // Build the remainder from everything AFTER the first
                    // segment, not the whole current pathname — otherwise a
                    // stale/old account slug already in the URL (e.g. after
                    // an account rename) gets a NEW slug prepended on top of
                    // it instead of being replaced (e.g. /new/old/accounts).
                    const restSegments = segments.slice(1);
                    const remainder = restSegments.length > 0 ? '/' + restSegments.join('/') : '';
                    window.location.replace(`/${encodeURIComponent(accountSlug)}${remainder}`);
                    return;
                }

                sessionStorage.removeItem('accountResolverRedirectTarget');
                sessionStorage.setItem('accountName', accountName);
                sessionStorage.setItem('accountLogo', data.logoImage ?? '');
                setStatus('ok');
            } catch (e) {
                console.error('[AccountResolver] resolve() threw:', e);
                setStatus('denied');
            }
        };

        resolve();
    }, [account, instance, inProgress]);

    if (status === 'checking') return <Loading />;
    if (status === 'denied') return <AccessDenied />;
    return <>{children}</>;
};